/**
 * Passkeys management card — shows the user's registered passkeys and lets
 * them add a new one (Face ID / Touch ID / Windows Hello / security key)
 * or remove existing ones.
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Plus, Loader2, Trash2, ShieldCheck, Smartphone, Usb } from "lucide-react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Credential {
  credential_id: string;
  label: string;
  transports: string[];
  device_type: string;
  backed_up: boolean;
  created_at: string;
  last_used_at: string | null;
}

const transportIcon = (transports: string[]) => {
  if (!transports || transports.length === 0) return <KeyRound className="h-4 w-4" />;
  if (transports.includes("internal")) return <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />;
  if (transports.includes("hybrid")) return <Smartphone className="h-4 w-4 text-sky-600 dark:text-sky-400" />;
  if (transports.includes("usb")) return <Usb className="h-4 w-4 text-slate-600 dark:text-slate-400" />;
  return <KeyRound className="h-4 w-4" />;
};

export const PasskeysManageCard: React.FC = () => {
  const qc = useQueryClient();
  const supported = typeof window !== "undefined" && browserSupportsWebAuthn();

  const list = useQuery<{ items: Credential[] }>({
    queryKey: ["passkeys-list"],
    queryFn: async () => (await api.get("/passkey/credentials")).data,
  });

  const [adding, setAdding] = useState(false);

  const add = async () => {
    setAdding(true);
    try {
      const { data: begin } = await api.post("/passkey/registration/begin", { origin: window.location.origin });
      const credential = await startRegistration({ optionsJSON: begin.optionsJSON });
      await api.post("/passkey/registration/complete", {
        ceremonyId: begin.ceremonyId,
        credential,
        origin: window.location.origin,
      });
      toast.success("Passkey added — you can now sign in without a password.");
      qc.invalidateQueries({ queryKey: ["passkeys-list"] });
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "";
      if (typeof msg === "string" && (msg.toLowerCase().includes("cancel") || msg.includes("NotAllowedError"))) {
        // silent cancel
      } else {
        toast.error(msg || "Could not add passkey");
      }
    } finally {
      setAdding(false);
    }
  };

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/passkey/credentials/${encodeURIComponent(id)}`)).data,
    onSuccess: () => {
      toast.success("Passkey removed");
      qc.invalidateQueries({ queryKey: ["passkeys-list"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not remove"),
  });

  const items = list.data?.items || [];

  return (
    <div className="sg-card p-6" data-testid="passkeys-manage-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
          <KeyRound className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Passkeys</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Sign in without a password using Face ID, Touch ID, Windows Hello, or a security key. Passkeys are stronger than passwords and can't be phished.
          </div>
        </div>
        <Button
          onClick={add}
          disabled={adding || !supported}
          data-testid="passkey-add-btn"
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black dark:bg-white dark:text-[color:var(--sg-fg)] dark:hover:bg-slate-200"
        >
          {adding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
          Add passkey
        </Button>
      </div>

      {!supported && (
        <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2 mb-3">
          This browser doesn't support passkeys. Try the latest Chrome, Safari, Edge, or Firefox.
        </div>
      )}

      {list.isLoading ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-6 text-center border border-dashed border-[color:var(--sg-border)] rounded-xl">
          No passkeys yet. Add one to skip your password next time.
        </div>
      ) : (
        <div className="divide-y divide-[color:var(--sg-border)]" data-testid="passkeys-list">
          {items.map((c) => (
            <div key={c.credential_id} className="py-3 flex items-center gap-3" data-testid={`passkey-item-${c.credential_id}`}>
              <div className="h-9 w-9 rounded-lg bg-[color:var(--sg-muted-bg)] flex items-center justify-center shrink-0">
                {transportIcon(c.transports)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[color:var(--sg-fg)] truncate">{c.label || "Passkey"}</div>
                <div className="text-[11px] text-[color:var(--sg-muted)]">
                  Added {new Date(c.created_at).toLocaleDateString()}
                  {c.last_used_at && ` · Last used ${new Date(c.last_used_at).toLocaleDateString()}`}
                  {c.backed_up && " · Synced across devices"}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Remove "${c.label || "this passkey"}"?`)) remove.mutate(c.credential_id);
                }}
                data-testid={`passkey-remove-${c.credential_id}`}
                className="text-rose-600 dark:text-rose-400 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasskeysManageCard;
